import React from 'react';
import { Box, Grid, TextField, styled } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import enGB from 'date-fns/locale/en-GB';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import theme from '../../../config/Theme';

const useStyles = makeStyles(() => ({
    root: {
        width: '100%',
    },
}));

const Spacer = styled(Box)(({ theme }: { theme: Theme }) => ({
    width: '100%',
    height: theme.spacing(2),
    backgroundColor: theme.palette.grey[200],
}));

interface IDateTimeRangeProps {
    from: number | undefined,
    to: number | undefined,
    onFromDateChange: (date: number) => void,
    onToDateChange: (date: number) => void
}

const DateTimeRange: React.FC<IDateTimeRangeProps> = (props) => {
    const classes = useStyles();

    const toLocalDate = (utcTs: number | undefined): Date | null => {
        if (utcTs === null || utcTs === undefined || Number.isNaN(utcTs)) return null;
        const d = new Date(utcTs);
        return new Date(
            d.getUTCFullYear(),
            d.getUTCMonth(),
            d.getUTCDate(),
            d.getUTCHours(),
            d.getUTCMinutes(),
            d.getUTCSeconds(),
            d.getUTCMilliseconds()
        );
    };

    const toUTCTimestamp = (date: Date | null | undefined): number | undefined => {
        if (!date) return undefined;
        return Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds(),
            date.getMilliseconds()
        );
    };

    const handleChange = (date: Date | null, setter: (date: number) => void) => {
        const ts = toUTCTimestamp(date);
        if (ts !== undefined) {
            setter(ts);
        }
    };

    return (
        <Grid container>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                <Grid item xs={12}>
                    <DateTimePicker
                        PaperProps={{
                            sx: {
                                '& .MuiClockNumber-root': {
                                    color: theme.palette.text.primary,
                                },
                            },
                        }}
                        className={classes.root}
                        label="From"
                        value={toLocalDate(props.from)}
                        onChange={(date) => handleChange(date, props.onFromDateChange)}
                        maxDateTime={toLocalDate(props.to)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                fullWidth
                            />
                        )}
                    />
                </Grid>
                <Spacer />
                <Grid item xs={12} style={{ paddingTop: '0rem' }}>
                    <DateTimePicker
                        PaperProps={{
                            sx: {
                                '& .MuiClockNumber-root': {
                                    color: theme.palette.text.primary,
                                },
                            },
                        }}
                        className={classes.root}
                        label="To"
                        value={toLocalDate(props.to)}
                        onChange={(date) => handleChange(date, props.onToDateChange)}
                        minDateTime={toLocalDate(props.from)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                fullWidth
                            />
                        )}
                    />
                </Grid>
            </LocalizationProvider>
        </Grid>
    );
};

export default DateTimeRange;
